import React, { useEffect, memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import { useRouter } from 'next/router';
import _ from 'lodash';
import {
  getAgentTipsRequest,
  updateFlightInAgentTipRequest,
  updateHotelInAgentTipRequest,
} from '../../../store/slices/intelligence';
import { SearchStatus, TravelTypes } from '../../../constants';
import AgentTipListItem from './AgentTipListItem';
import AgentTipsDebugInfo from './AgentTipsDebugInfo';

const AgentTipInformation = ({
  itineraryId, tripId,
  handleViewCabinsClick, handleCloseCruiseModal, currentOpenItem,
  doViewDebugInfo, travelOption,
}) => {
  const {
    itineraryDepartCity,
    itineraryDepartCityId: departureCityId,
  } = travelOption || {};
  const {
    itineraryDepartDate: cruiseDepartDate,
    itineraryReturnDate: cruiseReturnDate,
  } = (travelOption
    .itineraryRows
    .find((i) => i.itineraryId === itineraryId) || {});
  const dispatch = useDispatch();
  const router = useRouter();
  const { doRunTipsInParallel } = router.query;
  const searchObj = useSelector((state) => state.tripSearch.searchObj.cruise);
  const {
    value: flightOriginGeoIndex,
  } = searchObj.filters.origin || {};
  const hasFlightOrigin = flightOriginGeoIndex;
  const agentTips = useSelector((state) => state.intelligence.agentTips[`${itineraryId}-${flightOriginGeoIndex}`]);
  const [viewingTipId, setViewingTipId] = useState(typeof currentOpenItem === 'number' ? currentOpenItem : null);

  useEffect(() => {
    if (!agentTips) {
      dispatch(getAgentTipsRequest({
        cruiseDepartDate,
        cruiseReturnDate,
        cruiseItineraryId: itineraryId,
        cruiseDepartureCityId: departureCityId,
        doRunTipsInParallel,
      }));
    }
  }, [cruiseDepartDate, cruiseReturnDate, itineraryId, departureCityId,
    agentTips, dispatch, doRunTipsInParallel]);

  const handleToggleTipDetailClick = (index) => {
    if (index === viewingTipId) {
      setViewingTipId(null);
    } else {
      setViewingTipId(index);

      const hotelItinerary = agentTips.tips[index] && agentTips.tips[index].itineraries
        && _.find(agentTips.tips[index].itineraries, (i) => i.mode === TravelTypes.hotel);
      if (hotelItinerary
        && hotelItinerary.searchGeoname
        && hotelItinerary.searchGeoname.departureCityId
        && agentTips.tips[index]
        && agentTips.tips[index].hotelStatus !== SearchStatus.SEARCHING
        && agentTips.tips[index].hotelStatus !== SearchStatus.DONE) {
        dispatch(updateHotelInAgentTipRequest({
          cruiseItineraryId: itineraryId,
          cruiseDepartureCityId: hotelItinerary.searchGeoname.departureCityId,
          agentTipId: index,
          checkin: hotelItinerary.startDateTime,
          checkout: hotelItinerary.endDateTime,
          hotelId: hotelItinerary.hotel.ids,
          flightOriginGeoIndex,
          searchGeoname: hotelItinerary.searchGeoname,
        }));
      }

      const betterFlight = agentTips.tips[index]
        && !_.find(agentTips.tips[index].itineraries, (i) => i.mode === TravelTypes.flight)
        && agentTips.tips[index].betterFlight;
      if (betterFlight
        && agentTips.tips[index].flightStatus !== SearchStatus.SEARCHING
        && agentTips.tips[index].flightStatus !== SearchStatus.DONE) {
        dispatch(updateFlightInAgentTipRequest({
          cruiseItineraryId: itineraryId,
          agentTipId: index,
          flightOriginGeoIndex,
          betterFlight,
        }));
      }
    }
  };

  return (
    <div className="mx-10 my-5" style={{ minHeight: '300px' }}>
      {hasFlightOrigin && agentTips && (agentTips.tips || []).length > 0
        && agentTips.tips.map((agentTip, index) => (
          <AgentTipListItem
            // eslint-disable-next-line react/no-array-index-key
            key={`${index}-${agentTip.tipDescription}`}
            agentTip={agentTip}
            agentTipIndex={index}
            isViewing={viewingTipId === index}
            onToggleTipDetails={() => handleToggleTipDetailClick(index)}
            tripId={tripId}
            departureCityId={departureCityId}
            cruiseDepartDate={cruiseDepartDate}
            cruiseReturnDate={cruiseReturnDate}
            itineraryDepartCity={itineraryDepartCity}
            handleViewCabinsClick={handleViewCabinsClick}
            handleCloseCruiseModal={handleCloseCruiseModal}
          />
        ))}
      {doViewDebugInfo && hasFlightOrigin && agentTips && agentTips.debuggingInfo && (
        <AgentTipsDebugInfo debuggerInfo={agentTips.debuggingInfo} />
      )}
      {hasFlightOrigin
        && (!agentTips
          || agentTips.status === SearchStatus.INITIAL
          || agentTips.status === SearchStatus.SEARCHING)
        && (
          <div className="text-center">
            <CircularProgress color="secondary" />
          </div>
        )}

      {hasFlightOrigin
        && agentTips
        && agentTips.status === SearchStatus.DONE
        && !(agentTips.tips || []).length > 0
        && (
          <div className="text-center">
            No tips found!
          </div>
        )}

      {!hasFlightOrigin
        && (
          <span>
            Please specify a flight origin in filters above to use the Agent Tips feature.
          </span>
        )}
    </div>
  );
};

AgentTipInformation.propTypes = {
  itineraryId: PropTypes.string.isRequired,
  tripId: PropTypes.string.isRequired,
  handleViewCabinsClick: PropTypes.func.isRequired,
  handleCloseCruiseModal: PropTypes.func.isRequired,
  currentOpenItem: PropTypes.number,
  doViewDebugInfo: PropTypes.bool,
  travelOption: PropTypes.shape({
    itineraryRows: PropTypes.arrayOf(PropTypes.shape({})),
    itineraryDepartCity: PropTypes.string,
    itineraryDepartCityId: PropTypes.string,
  }).isRequired,
};

AgentTipInformation.defaultProps = {
  currentOpenItem: undefined,
  doViewDebugInfo: false,
};

export default memo(AgentTipInformation);
